import {Banner, Button, Dialog, IconButton, toast} from '@wandb/components';
import copyToClipboard from 'copy-to-clipboard';
import React, {useState} from 'react';

import {TaskState, useGetTaskQuery} from '../generated/graphql';
import {maybePluralize} from '../util/uihelpers';
import {TailwindContents} from '../weave/components/Tailwind';
import {type Project, ProjectPicker} from './ProjectPicker';
import {useRunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';

const {Content, Footer, Heading, Modal, Popover, Trigger} = Dialog;

interface RunMoverProps {
  entityName: string;
  requireSameEntity?: boolean;
  requireSameEntityReason?: string;
  projectInternalId: string;
  activeRunsCount?: number;
  selectedCount: number;
  refetch(): void;
  onMove(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string>;
}

export function RunMover({
  entityName,
  requireSameEntity,
  requireSameEntityReason,
  projectInternalId,
  activeRunsCount = 0,
  selectedCount,
  refetch,
  onMove,
}: RunMoverProps) {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [inProgressTaskID, setInProgressTaskID] = useState<string | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const handleSetProject = (project: Project) => {
    setSelectedProject(project);
    setPopoverOpen(false);
  };

  if (selectedCount === 0) {
    return null;
  }

  const handleMoveRuns = async () => {
    if (!selectedProject) {
      return;
    }
    try {
      const taskId = await onMove(
        selectedProject.entityName,
        selectedProject.name
      );
      setInProgressTaskID(taskId);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSelectedProject(null);
    }
  };

  const trigger = inProgressTaskID ? (
    <RunMoverProgress
      inProgressTaskID={inProgressTaskID}
      onFinished={() => {
        setInProgressTaskID(null);
        refetch();
        toast.success('Moved selected runs to a new project.');
      }}
      onError={(taskId, result) => {
        toast.error(
          `There was an error moving some of your runs: ${result}. ` +
            `If this persists, contact support with the the task id ${taskId}`,
          {
            action: {
              label: 'Copy task ID',
              onPress: () => copyToClipboard(taskId),
            },
          }
        );
      }}
    />
  ) : (
    <Button
      startIcon="folder-project-move"
      variant="secondary"
      onPress={() => {
        window.analytics?.track('Move button clicked', {
          location: analyticsLocation,
        });
      }}>
      Move to project
    </Button>
  );

  const excludedProjects = [{internalId: projectInternalId}] as Project[];

  return (
    <>
      <Trigger isOpen={popoverOpen} onOpenChange={setPopoverOpen}>
        {trigger}
        <Popover>
          <TailwindContents>
            <div className="p-16 min-w-[300px] flex flex-col gap-8">
              <ProjectPicker
                aria-label="Projects"
                entityName={entityName}
                exclude={excludedProjects}
                fullWidth
                onSelectProject={handleSetProject}
                selectedProject={selectedProject}
                requireSameEntity={requireSameEntity}
              />
              {requireSameEntity ? (
                <p className="text-sm">{requireSameEntityReason}</p>
              ) : null}
            </div>
          </TailwindContents>
        </Popover>
      </Trigger>
      <Modal
        isOpen={Boolean(selectedProject)}
        onOpenChange={() => setSelectedProject(null)}>
        {({close}) => (
          <>
            <Heading>Move {maybePluralize(selectedCount, 'run')}</Heading>
            <Content>
              <TailwindContents>
                <div className="flex flex-col gap-16">
                  <p>
                    Are you sure you want to move{' '}
                    {maybePluralize(selectedCount, 'run')} to project{' '}
                    <strong className="whitespace-nowrap">
                      {selectedProject?.entityName}/{selectedProject?.name}
                    </strong>
                  </p>
                  {activeRunsCount ? (
                    <Banner variant="warning" isDismissible={false}>
                      <Banner.Body>
                        {maybePluralize(activeRunsCount, 'run')} selected{' '}
                        {activeRunsCount === 1 ? 'is' : 'are'} still active.
                        Moving active runs may cause data loss or logging
                        errors.
                      </Banner.Body>
                    </Banner>
                  ) : null}
                  <p>
                    <em>
                      Note: Artifacts associated with the runs will not be
                      moved.
                    </em>
                  </p>
                </div>
              </TailwindContents>
            </Content>
            <Footer>
              <Button startIcon="folder-project-move" onPress={handleMoveRuns}>
                Move runs
              </Button>
              <Button variant="secondary" onPress={close}>
                Cancel
              </Button>
            </Footer>
          </>
        )}
      </Modal>
    </>
  );
}

interface RunMoverProgressProps {
  inProgressTaskID: string;
  onFinished: () => void;
  onError: (taskID: string, result?: string) => void;
}

function RunMoverProgress({
  inProgressTaskID,
  onFinished,
  onError,
}: RunMoverProgressProps) {
  'use memo';

  const [progressText, setProgressText] = useState('Moving...');

  useGetTaskQuery({
    pollInterval: 500,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({task}) => {
      const {state, progress, result} = task!;

      setProgressText(`Moving... (${progress}%)`);

      if (state === TaskState.Finished) {
        onFinished();
      } else if (state === TaskState.Failed) {
        onError(inProgressTaskID, result ?? undefined);
      }
    },
    onError: () => onError(inProgressTaskID),
    variables: {
      id: inProgressTaskID,
    },
  });

  return (
    <Button isDisabled startIcon="folder-project-move" variant="ghost">
      {progressText}
    </Button>
  );
}
